/*
XOL-67 (B4): the auto-save of an RTA form, as one small object. The client's response (Client Status and
Remarks, PUT /candidate/{id}/response) uses it now; the Tower Co decision (B5) and Step 5 (B6) use the same
one. It is NOT `requestRowSync`: an RTA has no row to register, no field entries, no 428 handshake, and its
conflicts are caught by the commands' own `expected*` / `version` checks (plan 3).

  var save = createAutosave({
    send: function (values) { return Promise },      // resolves with the answer, REJECTS on a refusal
    onSaved: function (answer, values) {},           // after each save the server took
    onRefused: function (err, values) {},            // after each refused save, once
    keepalive: function (values, ctx) { return Promise }, // optional: the page is being left (fetch keepalive)
    keepaliveWhileSaving: true,                      // optional: also send on page leave while a save is in flight (see below)
    pauseMs: 3000,                                   // optional; requestRowSync.PAUSE_MS
    savedText: 'Saved.'                              // optional; what the C-12 card says when the cycle is over
  })

  save.changed(values)                  restart the pause; the values go out when it ends
  save.changed(values, { immediate })   go out now (a Client Status change, decision 47)
  save.flush()                          send what is pending now; resolves { ok, answer } once it is all settled
  save.refused()                        true while the last save was refused and nothing newer waits (see below)
  save.lastRefusal()                    the error of that refusal (what `send` rejected with), else null
  save.retry()                          send the refused values once more, now; resolves like flush()
  save.cancel()                         drop what is pending (the form was reset)
  save.destroy()                        cancel, and stop reporting to the card
  save.state                            Vue.observable { pending, saving, error }

One request in flight at a time: a change that arrives during a save is sent after it, and only the newest
values go out (the server takes a whole response, not a patch). A refused save keeps the local values (the
form still shows them) and reports ONCE; it is not retried by itself, the next change is sent as usual. The form
STAYS refused until a later save of it succeeds (or `cancel()` resets the form to what the server holds): `flush()` resolves
{ ok: false } while it is refused, so every command that uses the flush as its gate (Complete, Transition, Unlock, Lock,
Close, Submit) refuses too, instead of acting on what the server holds while the screen shows something else (XOL-67 B10).
Every cycle is reported to the C-12 save-status card (requestRowSync.setExternalActivity): "Changes pending · saves
when you pause", "Saving…", and "Saved." when nothing is left.

Page leave while a save is IN FLIGHT (`keepaliveWhileSaving`, XOL-67 B5): the running request may die with the page, so `keepalive`
is called as well, with `ctx.after` = the values of the running save when newer values wait (the caller bases them on what the running
save will make the server hold), or `null` when nothing newer waits (the same values again: the API answers an identical decision with a
replay, 200 and nothing written, and a changed one with 409 stale_decision, which is harmless). Without the flag a form with a save in
flight sends nothing on page leave, as before.

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

import Vue from 'vue'
import * as sync from '@/lib/requestRowSync'
import * as popup from '@/lib/savePopup'

var counter = 0

function noop () {}

export function createAutosave (opts) {
  opts = opts || {}

  var key = 'rta-autosave-' + (++counter)
  var pauseMs = typeof opts.pauseMs === 'number' ? opts.pauseMs : sync.PAUSE_MS
  var savedText = opts.savedText || 'Saved.'

  var state = Vue.observable({ pending: false, saving: false, error: null })
  var timer = null
  var latest = null
  var dirty = false
  var wantNow = false
  var running = false
  var pauseStartedAt = 0
  var waiters = []
  var lastOk = true
  var lastAnswer = null
  var refusal = null
  var destroyed = false
  var runningValues = null

  function report () {
    if (destroyed) {
      sync.setExternalActivity(key, null)
      return
    }

    sync.setExternalActivity(key, {
      pending: state.pending,
      saving: state.saving,
      pauseStartedAt: pauseStartedAt,
      pauseMs: pauseMs,
      keepaliveSaving: !!opts.keepaliveWhileSaving
    }, typeof opts.keepalive === 'function' ? startKeepalive : null)
  }

  function clearTimer () {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  // Everything that was waiting for the cycle to end can go on.
  function settle () {
    var done = waiters
    waiters = []
    done.forEach(function (resolve) { resolve({ ok: lastOk, answer: lastAnswer }) })
  }

  // Nothing pending, nothing running, no pause left: the cycle is over.
  function idle () {
    return !dirty && !running && !timer
  }

  function start () {
    var values = latest

    dirty = false
    wantNow = false
    clearTimer()
    running = true
    runningValues = values
    state.pending = false
    state.saving = true
    state.error = null
    report()

    var call

    try {
      call = Promise.resolve(opts.send(values))
    } catch (e) {
      call = Promise.reject(e)
    }

    call.then(function (answer) {
      lastOk = true
      refusal = null
      lastAnswer = answer
      running = false
      runningValues = null
      state.saving = false

      try {
        (opts.onSaved || noop)(answer, values)
      } catch (e) {
        console.error('rtaAutosave: onSaved threw', e)
      }

      next(true)
    }, function (err) {
      lastOk = false
      refusal = err || true
      running = false
      runningValues = null
      state.saving = false
      state.error = err || true

      try {
        (opts.onRefused || noop)(err, values)
      } catch (e) {
        console.error('rtaAutosave: onRefused threw', e)
      }

      next(false)
    })
  }

  // A save ended. Newer values go out (at once when asked to, else after their own pause).
  function next (ok) {
    if (dirty) {
      if (wantNow) {
        start()
        return
      }

      state.pending = true
    } else {
      state.pending = !!timer
    }

    report()

    if (idle()) {
      // A save that SENT NOTHING because every edit was held back (its answer says `held: true`, XOL-67 V3) is not "Saved.": the card stays as it is.
      if (ok && !destroyed && !(lastAnswer && lastAnswer.held === true)) {
        popup.success(savedText)
      }

      settle()
    }
  }

  function changed (values, o) {
    if (destroyed) {
      return
    }

    latest = values
    dirty = true
    state.error = null

    if (o && o.immediate) {
      wantNow = true
      clearTimer()

      if (running) {
        // It goes out when the save in flight ends.
        state.pending = true
        report()
        return
      }

      start()
      return
    }

    // The pause restarts on every change.
    clearTimer()
    state.pending = true
    pauseStartedAt = Date.now()
    report()

    timer = setTimeout(function () {
      timer = null

      if (!dirty) {
        return
      }

      if (running) {
        wantNow = true
        return
      }

      start()
    }, pauseMs)
  }

  function flush () {
    clearTimer()

    if (dirty && !running) {
      return new Promise(function (resolve) {
        waiters.push(resolve)
        start()
      })
    }

    if (running || dirty) {
      return new Promise(function (resolve) {
        // Newer values wait for the save in flight; they go out the moment it ends.
        wantNow = dirty
        waiters.push(resolve)
      })
    }

    // Nothing waiting, nothing running: nothing to send. The answer is the last save's: a refusal stands (the screen holds values the
    // server refused) until a later save of the form succeeds or the form is reset (`cancel`).
    return Promise.resolve({ ok: lastOk, answer: lastAnswer })
  }

  function refused () {
    return !lastOk && idle()
  }

  function lastRefusal () {
    return refused() ? refusal : null
  }

  // A command that gates on the form was stopped by its refusal and the refusal is not one the user can fix by editing (a 5xx, a network
  // error): send the values the server never took, once. Resolves like flush(): { ok } of that save.
  function retry () {
    if (!refused() || latest === null) {
      return flush()
    }

    changed(latest, { immediate: true })

    return flush()
  }

  function cancel () {
    clearTimer()
    dirty = false
    wantNow = false
    latest = null
    state.pending = false
    report()

    if (!running) {
      settle()

      // The form was reset to what the server holds: there is nothing left to be refused. (Waiters above were told the truth first.)
      lastOk = true
      refusal = null
      state.error = null
    }
  }

  function destroy () {
    cancel()
    destroyed = true
    report()
  }

  // The page is being left (beforeunload / pagehide): what is pending goes out as a fetch keepalive request.
  function startKeepalive () {
    if (typeof opts.keepalive !== 'function') {
      return Promise.resolve()
    }

    var values = latest

    if (running) {
      // A save is in flight: only a form that asked for it also sends now (see the header).
      if (!opts.keepaliveWhileSaving) {
        return Promise.resolve()
      }

      var after = dirty ? runningValues : null

      if (dirty) {
        dirty = false
        wantNow = false
        clearTimer()
        state.pending = false
        report()
      }

      try {
        return Promise.resolve(opts.keepalive(values, { after: after })).catch(noop)
      } catch (e) {
        return Promise.resolve()
      }
    }

    if (!dirty) {
      return Promise.resolve()
    }

    dirty = false
    wantNow = false
    clearTimer()
    state.pending = false
    report()

    try {
      return Promise.resolve(opts.keepalive(values)).catch(noop)
    } catch (e) {
      return Promise.resolve()
    }
  }

  return {
    state: state,
    changed: changed,
    flush: flush,
    refused: refused,
    lastRefusal: lastRefusal,
    retry: retry,
    cancel: cancel,
    destroy: destroy,
    keepalive: startKeepalive
  }
}
